import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, MessageSquareWarning, X, PenLine } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import { formatTime12 } from "@/lib/format";
import Logo from "@/components/Logo";
import SignaturePad from "@/components/SignaturePad";

const STATUS_STYLES = {
  Draft: "text-gray-300 bg-gray-400/10 border-gray-400/30",
  Sent: "text-blue-300 bg-blue-400/10 border-blue-400/30",
  Approved: "text-[#C9963F] bg-[#C9963F]/10 border-[#C9963F]/40",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return <span className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${cls}`}>{status}</span>;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return s; }
}
function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function CustomerQuotePortal() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [changeMsg, setChangeMsg] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [signedByName, setSignedByName] = useState("");
  const sigRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`${API}/public/quotes/${token}`);
      setQuote(data);
      setSignedByName((prev) => prev || data.client_name || "");
    } catch (e) { setError(e?.response?.data?.detail || "Quote not found"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [token]);

  async function approve() {
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please sign on the pad before approving");
    if (!signedByName.trim()) return toast.error("Please type your full name");
    setSubmitting(true);
    try {
      await publicApi.post(`${API}/public/quotes/${token}/approve`, {
        signature_image: sigRef.current.toDataURL(), signed_by_name: signedByName.trim(),
      });
      toast.success("Quote signed & approved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
    finally { setSubmitting(false); }
  }

  async function submitChange() {
    if (!changeMsg.trim()) return toast.error("Please describe the change");
    try {
      await publicApi.post(`${API}/public/quotes/${token}/change-request`, {
        message: changeMsg.trim(), requester_name: requesterName.trim(),
      });
      toast.success("Change request sent");
      setShowChange(false); setChangeMsg("");
    } catch { toast.error("Failed"); }
  }

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/public/quotes/${token}/pdf`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sheesh-quote-${(quote?.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  }

  if (loading) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center"><div className="text-white/40 text-sm uppercase tracking-widest">Loading quote…</div></div>;
  if (error || !quote) return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center px-4">
      <Logo size="md" /><div className="mt-8 text-white/60">{error}</div>
    </div>
  );

  const services = [];
  if (quote.catering_food) services.push("Food Catering");
  if (quote.catering_mocktail) services.push("Mocktail Bar");
  if (quote.catering_hookah) services.push("Hookah Service");
  const isApproved = quote.status === "Approved";

  return (
    <div className="min-h-screen bg-[#050505] text-white relative" data-testid="customer-quote-portal">
      <div className="absolute inset-x-0 top-0 h-72 opacity-30 bg-cover bg-center"
           style={{ backgroundImage: "url('https://images.pexels.com/photos/24863057/pexels-photo-24863057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
      <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <header className="text-center mb-10 fade-up">
          <Logo size="lg" />
          <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-white/50">Catering Quote</div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <StatusBadge status={quote.status} />
            {quote.expires_at && <span className="text-[10px] uppercase tracking-wider text-white/40">Valid until {new Date(quote.expires_at).toLocaleDateString()}</span>}
          </div>
        </header>

        {isApproved && (
          <div className="bg-[#C9963F]/10 border border-[#C9963F]/40 p-5 mb-6 flex items-start gap-3 fade-up" data-testid="approved-banner">
            <CheckCircle2 size={20} className="text-[#C9963F] mt-0.5" />
            <div className="flex-1">
              <div className="text-[#C9963F] font-medium">Quote Approved</div>
              <div className="text-white/70 text-sm mt-0.5">{quote.signed_by_name ? `Signed by ${quote.signed_by_name} on ${new Date(quote.signed_at).toLocaleString()}` : `Approved on ${new Date(quote.approved_at).toLocaleString()}`}. We'll prepare your contract shortly.</div>
            </div>
            <button onClick={downloadPdf} data-testid="approved-download-pdf-btn" className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
              <Download size={14} /> Download PDF
            </button>
          </div>
        )}
        {/* Paper-look quote */}
        <article className="bg-white text-[#0a0a0a] p-8 sm:p-12 shadow-2xl fade-up">
          <div className="flex items-start justify-between border-b-2 border-[#C9963F] pb-4 mb-6">
            <div>
              <div className="font-serif-brand text-2xl sm:text-3xl text-[#0a0a0a] italic">Sheesh Eatery & Lounge</div>
              <div className="text-xs text-gray-600 mt-1">4707 State Hwy 121, The Colony, TX 75056</div>
              <div className="text-xs text-gray-600">(214) 310-7650 · sheeshtx@gmail.com</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Quote</div>
              <div className="text-xs text-gray-700 mt-1">{new Date(quote.created_at).toLocaleDateString()}</div>
              {quote.expires_at && <div className="text-xs text-gray-500">Valid until {new Date(quote.expires_at).toLocaleDateString()}</div>}
            </div>
          </div>

          <h1 className="font-serif-brand text-3xl mb-6">Catering Quote</h1>

          <Section2 title="Client">
            <Pair label="Name" value={quote.client_name} />
            <Pair label="Phone" value={quote.client_phone || "—"} />
            <Pair label="Email" value={quote.client_email || "—"} />
          </Section2>

          <Section2 title="Event">
            <Pair label="Date" value={fmtDate(quote.event_date)} />
            <Pair label="Time" value={formatTime12(quote.event_time) || "—"} />
            <Pair label="Location" value={quote.event_location || "—"} full />
            <Pair label="Services" value={services.join(", ") || "—"} full />
          </Section2>

          <Section2 title="Menu & Package">
            <p className="whitespace-pre-wrap text-sm leading-relaxed sm:col-span-2">{quote.menu_description || "—"}</p>
          </Section2>

          {quote.addons && (
            <Section2 title="Add-ons / Service Type">
              <p className="whitespace-pre-wrap text-sm leading-relaxed sm:col-span-2">{quote.addons}</p>
            </Section2>
          )}

          <Section2 title="Pricing Estimate">
            <div className="text-sm sm:col-span-2">
              <Row label="Guest Count" value={quote.guest_count} />
              {(quote.line_items && quote.line_items.length > 0) ? (
                <>
                  {quote.line_items.map((it, idx) => {
                    const up = Number(it.unit_price) || 0;
                    const qty = it.kind === "per_person" ? Number(quote.guest_count) || 0 : Number(it.quantity) || 0;
                    const line = qty * up;
                    const detail = it.kind === "per_person"
                      ? `${qty} × ${money(up)}/person`
                      : (qty !== 1 ? `${qty} × ${money(up)}` : "");
                    return (
                      <div key={it.id || idx} className="flex items-baseline justify-between py-1.5 border-b border-gray-100">
                        <div>
                          <div className="text-gray-900 text-sm">{it.label || (it.kind === "per_person" ? "Per Person" : "Item")}</div>
                          {detail && <div className="text-[10px] uppercase tracking-wider text-gray-500">{detail}</div>}
                        </div>
                        <span className="text-gray-900">{money(line)}</span>
                      </div>
                    );
                  })}
                  <Row label="Subtotal" value={money(quote.subtotal)} />
                </>
              ) : (
                <>
                  <Row label="Per Person" value={money(quote.per_person_price)} />
                  <Row label="Subtotal" value={money(quote.subtotal)} />
                  <Row label="Service Charge" value={money(quote.service_charge)} />
                </>
              )}
              <Row label="Tax" value={money(quote.tax)} />
              <div className="border-t-2 border-[#C9963F] mt-3 pt-3 flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-gray-700">Estimated Total</span>
                <span className="font-serif-brand text-3xl text-[#0a0a0a]">{money(quote.total)}</span>
              </div>
            </div>
          </Section2>

          <p className="text-xs text-gray-500 italic mt-8">
            This quote is an estimate and is not a binding contract. Final pricing and terms are confirmed in
            the signed Catering Service Agreement.
          </p>

          <QuoteSignatures quote={quote} />
        </article>

        {!isApproved && (
          <div className="mt-8 bg-[#0e0e0e] border border-white/10 p-6 sm:p-8 fade-up" data-testid="quote-actions">
            <h3 className="font-serif-brand text-2xl mb-2">Sign to approve this quote</h3>
            <p className="text-white/50 text-sm mb-6">Sign with your finger on mobile or your mouse on desktop. Your signature locks in this estimate.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-2">
                <SignaturePad ref={sigRef} />
                <button type="button" onClick={() => sigRef.current?.clear()} data-testid="quote-clear-signature"
                  className="mt-2 text-xs text-white/50 hover:text-[#C9963F] transition-colors">Clear signature</button>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Type your full name</label>
                <input value={signedByName} onChange={(e) => setSignedByName(e.target.value)} placeholder="Full name"
                  data-testid="quote-signed-by-name"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button onClick={approve} disabled={submitting} data-testid="approve-quote-btn"
                className="bg-[#C9963F] text-black font-medium px-6 py-3 hover:bg-[#A67A2D] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <PenLine size={14} /> {submitting ? "Submitting…" : "Sign & Approve Quote"}
              </button>
              <button onClick={() => setShowChange(true)} data-testid="quote-request-change-btn"
                className="border border-white/20 text-white/80 px-6 py-3 hover:border-[#C9963F] hover:text-[#C9963F] transition-colors inline-flex items-center justify-center gap-2">
                <MessageSquareWarning size={14} /> Request a Change
              </button>
              <button onClick={downloadPdf} data-testid="quote-download-pdf-btn"
                className="border border-white/20 text-white/80 px-6 py-3 hover:border-[#C9963F] hover:text-[#C9963F] transition-colors inline-flex items-center justify-center gap-2">
                <Download size={14} /> Download PDF
              </button>
            </div>
          </div>
        )}

        {showChange && (
          <Modal onClose={() => setShowChange(false)} title="Request a Change" testid="quote-change-modal">
            <input data-testid="quote-change-name-input" value={requesterName} onChange={(e) => setRequesterName(e.target.value)} placeholder={quote.client_name}
              className="w-full bg-transparent border-b border-white/20 px-0 py-2 mb-4 focus:border-[#C9963F] focus:outline-none text-white" />
            <textarea data-testid="quote-change-message-input" rows={5} value={changeMsg} onChange={(e) => setChangeMsg(e.target.value)} placeholder="What would you like changed?"
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2.5 focus:border-[#C9963F] focus:outline-none text-white" />
            <div className="flex justify-end gap-3 mt-5">
              <button data-testid="quote-change-cancel-btn" onClick={() => setShowChange(false)} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
              <button data-testid="quote-change-send-btn" onClick={submitChange} className="bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm">Send</button>
            </div>
          </Modal>
        )}

        <footer className="text-center mt-12 text-[10px] uppercase tracking-[0.3em] text-white/30">
          Sheesh Eatery & Lounge · 4707 State Hwy 121 · The Colony, TX
        </footer>
      </div>
    </div>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-white/15 max-w-lg w-full p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <h3 className="font-serif-brand text-2xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}
function Section2({ title, children }) {
  return (
    <section className="mb-7">
      <h2 className="text-[10px] uppercase tracking-[0.25em] text-[#C9963F] mb-3 border-b border-gray-200 pb-2">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </section>
  );
}
function Pair({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (<div className="flex items-baseline justify-between py-1.5 border-b border-gray-100 last:border-b-0"><span className="text-gray-600 text-xs uppercase tracking-wider">{label}</span><span className="text-gray-900">{value}</span></div>);
}

function QuoteSignatures({ quote }) {
  const repDate = new Date(quote.created_at).toLocaleDateString();
  return (
    <section className="mt-10 pt-6 border-t border-gray-200" data-testid="quote-signature-block">
      <h2 className="text-[10px] uppercase tracking-[0.25em] text-[#C9963F] mb-6">Acceptance</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <div className="h-20 flex items-end border-b border-gray-900 pb-1">
            {quote.signature_image && <img src={quote.signature_image} alt="client signature" className="max-h-20" data-testid="quote-client-signature-img" />}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#C9963F] mt-2">Client Signature</div>
          <div className="text-sm text-gray-900">{quote.signed_by_name || "—"}</div>
          <div className="text-xs text-gray-500">Date: {quote.signed_at ? new Date(quote.signed_at).toLocaleDateString() : "—"}</div>
        </div>
        <div>
          <div className="h-20 flex items-end border-b border-gray-900 pb-1">
            <span className="font-serif-brand italic text-3xl text-[#0a0a0a]" data-testid="quote-rep-signature">Sheesh Eatery &amp; Lounge</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#C9963F] mt-2">Authorized Representative</div>
          <div className="text-sm text-gray-900">Sheesh Eatery &amp; Lounge</div>
          <div className="text-xs text-gray-500">Date: {repDate}</div>
        </div>
      </div>
    </section>
  );
}
